import { useState } from 'react';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import {
  Star, CheckCircle,
  X, SlidersHorizontal, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { LANDING_COLORS } from '@/constants/landing-colors';
import { INSTRUMENT_FILTER_ORDER, INSTRUMENTS } from '@/constants/instruments';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from '@/components/ui/sheet';
import DirectoryMapMini from './DirectoryMapMini';
import DirectoryMapModal from './DirectoryMapModal';
import { DirectoryPromoBannerB2C } from './DirectoryPromoBannerB2C';
import { DIRECTORY_TYPES, type DirectoryType } from '@/constants/directoryTypes';

interface ListingBase {
  id: string;
  name: string;
  slug: string;
  type: DirectoryType;
  city: string;
  address?: string;
  province?: string;
  latitude?: number | null;
  longitude?: number | null;
  rating?: number | null;
  review_count?: number;
  is_claimed?: boolean;
  is_premium?: boolean;
}

interface FilterState {
  types: string[];
  instruments: string[];
  minRating: number | null;
  claimed: boolean | null;
  sortBy: string;
  cities: string[];
}

type TypeCount = Record<DirectoryType, number>;

interface CityOption {
  city: string;
  count: number;
  province: string;
}

interface DirectoryFiltersSidebarProps {
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  totalResults: number;
  listings?: ListingBase[];
  typeCounts?: TypeCount;
  selectedCity?: string;
  customCenter?: { latitude: number; longitude: number; zoom?: number };
  availableCities?: CityOption[];
  className?: string;
}

const TYPE_OPTIONS = [
  { value: 'school', label: 'Escuelas de Música' },
  { value: 'rehearsal', label: 'Locales de Ensayo' },
  { value: 'store', label: 'Tiendas de Música' },
];

const RATING_OPTIONS = [
  { value: 4.5, label: 'Excelente: 4.5+' },
  { value: 4, label: 'Muy bueno: 4+' },
  { value: 3, label: 'Bueno: 3+' },
];

const colors = LANDING_COLORS.jams;

// Zonas/distritos que deben agruparse bajo su provincia principal
const PROVINCE_MAIN_CITIES: Record<string, string> = {
  'Madrid': 'Madrid',
  'Barcelona': 'Barcelona',
  'Valencia': 'Valencia',
  'Sevilla': 'Sevilla',
};

// Función para agrupar ciudades por provincia
function groupCitiesByProvince(cities: CityOption[]): CityOption[] {
  const grouped: Record<string, { count: number; province: string }> = {};
  const standalone: CityOption[] = [];

  cities.forEach(c => {
    // Si la provincia es una ciudad principal conocida, agrupar bajo ella
    const mainCity = PROVINCE_MAIN_CITIES[c.province];
    if (mainCity && c.city !== mainCity) {
      // Es una zona/distrito de una ciudad principal
      if (!grouped[mainCity]) {
        grouped[mainCity] = { count: 0, province: c.province };
      }
      grouped[mainCity].count += c.count;
    } else if (mainCity && c.city === mainCity) {
      // Es la ciudad principal misma
      if (!grouped[mainCity]) {
        grouped[mainCity] = { count: c.count, province: c.province };
      } else {
        grouped[mainCity].count += c.count;
      }
    } else {
      // Ciudad independiente
      standalone.push(c);
    }
  });

  // Convertir agrupados a CityOption[]
  const groupedCities: CityOption[] = Object.entries(grouped).map(([city, data]) => ({
    city,
    count: data.count,
    province: data.province,
  }));

  // Combinar y ordenar por count
  return [...groupedCities, ...standalone].sort((a, b) => b.count - a.count);
}

function FilterContent({
  filters,
  onFilterChange,
  totalResults,
  typeCounts,
  availableCities,
  onClose
}: Omit<DirectoryFiltersSidebarProps, 'listings' | 'selectedCity' | 'customCenter' | 'className'> & { onClose?: () => void }) {
  const handleTypeToggle = (type: string) => {
    const newTypes = filters.types.includes(type)
      ? filters.types.filter(t => t !== type)
      : [...filters.types, type];
    onFilterChange({ ...filters, types: newTypes });
  };

  const handleRatingChange = (rating: number | null) => {
    onFilterChange({ ...filters, minRating: filters.minRating === rating ? null : rating });
  };

  const handleClaimedToggle = () => {
    onFilterChange({
      ...filters,
      claimed: filters.claimed === true ? null : true
    });
  };

  const handleInstrumentToggle = (instrument: string) => {
    const currentInstruments = filters.instruments || [];
    const newInstruments = currentInstruments.includes(instrument)
      ? currentInstruments.filter(i => i !== instrument)
      : [...currentInstruments, instrument];
    onFilterChange({ ...filters, instruments: newInstruments });
  };

  const handleCityToggle = (cityName: string) => {
    const currentCities = filters.cities || [];
    const newCities = currentCities.includes(cityName)
      ? currentCities.filter(c => c !== cityName)
      : [...currentCities, cityName];
    onFilterChange({ ...filters, cities: newCities });
  };

  const [showAllInstruments, setShowAllInstruments] = useState(false);
  const visibleInstruments = showAllInstruments
    ? INSTRUMENT_FILTER_ORDER
    : INSTRUMENT_FILTER_ORDER.slice(0, 5);

  const [showAllCities, setShowAllCities] = useState(false);
  // Agrupar zonas/distritos bajo sus ciudades principales
  const groupedCities = groupCitiesByProvince(availableCities || []);
  const topCities = groupedCities.slice(0, 10);
  const visibleCities = showAllCities ? groupedCities : topCities;

  return (
    <div className="space-y-6">
      {/* Section: Find your perfect place */}
      <div>
        <h3 className="font-semibold text-gray-900 mb-4">Encuentra tu espacio</h3>
      </div>

      {/* Popular Filters - Booking style */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Filtros populares</h4>
        <div className="space-y-2">
          {TYPE_OPTIONS.map(option => {
            const isChecked = filters.types.includes(option.value);
            const count = typeCounts?.[option.value as keyof TypeCount] || 0;

            return (
              <label
                key={option.value}
                className="flex items-center justify-between py-1.5 cursor-pointer group"
              >
                <div className="flex items-center gap-2.5">
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={() => handleTypeToggle(option.value)}
                    className="w-5 h-5 rounded border-gray-300 data-[state=checked]:bg-[#4F46E5] data-[state=checked]:border-[#4F46E5]"
                  />
                  <span className="text-sm text-gray-700 group-hover:text-gray-900">
                    {option.label}
                  </span>
                </div>
                <span className="text-sm text-gray-500">{count}</span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Instruments Filter */}
      <div className="pt-4 border-t border-gray-200">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Instrumentos</h4>
        <div className="space-y-2">
          {visibleInstruments.map(instrumentId => {
            const instrument = INSTRUMENTS[instrumentId];
            if (!instrument) return null;
            const Icon = instrument.icon;
            const isChecked = (filters.instruments || []).includes(instrumentId);

            return (
              <label
                key={instrumentId}
                className="flex items-center gap-2.5 py-1.5 cursor-pointer group"
              >
                <Checkbox
                  checked={isChecked}
                  onCheckedChange={() => handleInstrumentToggle(instrumentId)}
                  className="w-5 h-5 rounded border-gray-300 data-[state=checked]:bg-[#4F46E5] data-[state=checked]:border-[#4F46E5]"
                />
                <span className="text-sm text-gray-700 group-hover:text-gray-900 flex items-center gap-1.5">
                  <Icon className="w-3.5 h-3.5" style={{ color: instrument.color }} />
                  {instrument.label}
                </span>
              </label>
            );
          })}
        </div>
        {INSTRUMENT_FILTER_ORDER.length > 5 && (
          <button
            onClick={() => setShowAllInstruments(!showAllInstruments)}
            className="flex items-center gap-1 text-sm text-[#4F46E5] font-medium mt-2 hover:underline"
          >
            {showAllInstruments ? (
              <>
                <ChevronUp className="w-4 h-4" />
                Ver menos
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                Ver {INSTRUMENT_FILTER_ORDER.length - 5} más
              </>
            )}
          </button>
        )}
      </div>

      {/* Cities Filter */}
      {availableCities && availableCities.length > 0 && (
        <div className="pt-4 border-t border-gray-200">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Ciudades</h4>
          <div className="space-y-2">
            {visibleCities.map(cityOption => {
              const isChecked = (filters.cities || []).includes(cityOption.city);

              return (
                <label
                  key={cityOption.city}
                  className="flex items-center justify-between py-1.5 cursor-pointer group"
                >
                  <div className="flex items-center gap-2.5">
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={() => handleCityToggle(cityOption.city)}
                      className="w-5 h-5 rounded border-gray-300 data-[state=checked]:bg-[#4F46E5] data-[state=checked]:border-[#4F46E5]"
                    />
                    <span className="text-sm text-gray-700 group-hover:text-gray-900">
                      {cityOption.city}
                    </span>
                  </div>
                  <span className="text-sm text-gray-500">{cityOption.count}</span>
                </label>
              );
            })}
          </div>
          {groupedCities.length > 10 && (
            <button
              onClick={() => setShowAllCities(!showAllCities)}
              className="flex items-center gap-1 text-sm text-[#4F46E5] font-medium mt-2 hover:underline"
            >
              {showAllCities ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  Ver menos
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  Ver {groupedCities.length - 10} más
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Rating Filter */}
      <div className="pt-4 border-t border-gray-200">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Valoración</h4>
        <div className="space-y-2">
          {RATING_OPTIONS.map(option => {
            const isSelected = filters.minRating === option.value;

            return (
              <label
                key={option.value}
                className="flex items-center gap-2.5 py-1.5 cursor-pointer group"
              >
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => handleRatingChange(option.value)}
                  className="w-5 h-5 rounded border-gray-300 data-[state=checked]:bg-[#4F46E5] data-[state=checked]:border-[#4F46E5]"
                />
                <span className="text-sm text-gray-700 group-hover:text-gray-900 flex items-center gap-1">
                  <Star className="w-3.5 h-3.5 text-amber-400 fill-current" />
                  {option.label}
                </span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Verified Filter */}
      <div className="pt-4 border-t border-gray-200">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Estado</h4>
        <label className="flex items-center gap-2.5 py-1.5 cursor-pointer group">
          <Checkbox
            checked={filters.claimed === true}
            onCheckedChange={handleClaimedToggle}
            className="w-5 h-5 rounded border-gray-300 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
          />
          <span className="text-sm text-gray-700 group-hover:text-gray-900 flex items-center gap-1.5">
            <CheckCircle className={`w-4 h-4 ${filters.claimed ? 'text-emerald-600' : 'text-gray-400'}`} />
            Solo verificados
          </span>
        </label>
      </div>

      {/* Mobile: Apply button */}
      {onClose && (
        <div className="pt-4 border-t border-gray-200">
          <Button
            onClick={onClose}
            className="w-full h-11 rounded-lg font-semibold"
            style={{ backgroundColor: colors.primary }}
          >
            Ver {totalResults} resultados
          </Button>
        </div>
      )}
    </div>
  );
}

// Desktop Sidebar with Map Mini - Booking.com style
export function DirectoryFiltersSidebar(props: DirectoryFiltersSidebarProps) {
  const [showFullMap, setShowFullMap] = useState(false);
  const { listings = [], selectedCity, customCenter, typeCounts, availableCities, ...filterProps } = props;

  return (
    <>
      <aside className={`w-[350px] flex-shrink-0 ${props.className || ''}`}>
        <div className="sticky top-24 space-y-4">
          {/* Mini Map - Booking style */}
          {listings.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
              <DirectoryMapMini
                listings={listings}
                onExpand={() => setShowFullMap(true)}
                selectedCity={selectedCity}
                customCenter={customCenter}
              />
            </div>
          )}

          {/* Filters - Booking style */}
          <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
            <FilterContent {...filterProps} typeCounts={typeCounts} availableCities={availableCities} />
          </div>

          {/* Promo Banner B2C - Jams */}
          <DirectoryPromoBannerB2C />
        </div>
      </aside>

      {/* Full Map Modal - Using unified DirectoryMapModal */}
      <DirectoryMapModal
        open={showFullMap}
        onOpenChange={setShowFullMap}
        listings={listings}
        selectedCity={selectedCity}
      />
    </>
  );
}

// Mobile Filters Sheet
export function DirectoryFiltersSheet(props: Omit<DirectoryFiltersSidebarProps, 'listings' | 'selectedCity' | 'className' | 'customCenter'>) {
  const [open, setOpen] = useState(false);
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const { typeCounts, availableCities, ...filterProps } = props;

  const activeCount = [
    props.filters.types.length > 0 && props.filters.types.length < DIRECTORY_TYPES.length,
    props.filters.minRating !== null,
    props.filters.claimed !== null,
  ].filter(Boolean).length;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          className="rounded-xl h-9 px-4 border-gray-200 relative text-sm hover:border-gray-300"
        >
          <SlidersHorizontal className="w-4 h-4 mr-1.5" />
          Filtros
          {activeCount > 0 && (
            <span
              className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-white text-[10px] font-bold flex items-center justify-center"
              style={{ backgroundColor: colors.primary }}
            >
              {activeCount}
            </span>
          )}
        </Button>
      </SheetTrigger>
      {/* Desktop: panel lateral derecho. Móvil: bottom sheet. */}
      <SheetContent
        side={isDesktop ? 'right' : 'bottom'}
        className={isDesktop ? 'w-full sm:max-w-md flex flex-col p-0' : 'h-[85vh] rounded-t-3xl'}
        hideCloseButton
      >
        <SheetHeader className={`border-b border-gray-100 ${isDesktop ? 'p-5' : 'pb-4'}`}>
          <div className="flex items-center justify-between">
            <SheetTitle>Filtros</SheetTitle>
            <SheetClose asChild>
              <button className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                <X className="w-4 h-4" />
              </button>
            </SheetClose>
          </div>
        </SheetHeader>
        <div className={isDesktop ? 'flex-1 overflow-y-auto px-5 py-4' : 'py-4 overflow-y-auto max-h-[calc(85vh-120px)]'}>
          <FilterContent {...filterProps} typeCounts={typeCounts} availableCities={availableCities} onClose={() => setOpen(false)} />
        </div>
      </SheetContent>
    </Sheet>
  );
}

// Mobile Map Dialog - can be controlled externally or internally
// Now uses DirectoryMapModal for unified behavior
interface DirectoryMapSheetProps {
  listings?: ListingBase[];
  selectedCity?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  showTrigger?: boolean;
}

export function DirectoryMapSheet({
  listings = [],
  selectedCity,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  showTrigger = true
}: DirectoryMapSheetProps) {
  const [internalOpen, setInternalOpen] = useState(false);

  // Use controlled state if provided, otherwise use internal state
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setIsOpen = controlledOnOpenChange || setInternalOpen;

  return (
    <>
      {showTrigger && (
        <Button
          variant="outline"
          className="rounded-lg h-9 px-3 border-gray-300 text-sm"
          onClick={() => setIsOpen(true)}
        >
          <span className="mr-1.5">🗺️</span>
          Mapa
        </Button>
      )}
      <DirectoryMapModal
        open={isOpen}
        onOpenChange={setIsOpen}
        listings={listings}
        selectedCity={selectedCity}
      />
    </>
  );
}

export default DirectoryFiltersSidebar;
